import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LogError, LogStatus } from '@memberjunction/core';
import { ActiveUserService } from './active-user.service';
import { UserAuthService, PersonChapterLinkResult } from './user-auth.service';
import { DataFetchService } from './data-fetch.service';
import { QueryNames } from '../utils/constants';

/**
 * Service that manages committee access for the current active user
 * Handles both normal users and impersonated users to determine which committees
 * they have access to view data for
 */
@Injectable({
  providedIn: 'root'
})
export class UserCommitteeAccessService {
  private committeeAccessSubject = new BehaviorSubject<string[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits the current user's committee access list
   */
  public committeeAccess$ = this.committeeAccessSubject.asObservable();

  /**
   * Observable that indicates when committee access is being loaded
   */
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private activeUserService: ActiveUserService,
    private userAuthService: UserAuthService,
    private dataFetchService: DataFetchService
  ) {
    // Initialize service by loading committee access when active user changes
    this.initializeService();
  }

  /**
   * Gets the current user's committee access IDs
   * @returns Array of committee IDs the current user has access to
   */
  public getCommitteeAccess(): string[] {
    return this.committeeAccessSubject.value;
  }

  /**
   * Checks if the current user has access to a specific committee
   * @param committeeId - Committee ID to check
   * @returns True if user has access to the committee
   */
  public hasCommitteeAccess(committeeId: string): boolean {
    const accessList = this.getCommitteeAccess();
    return accessList.includes(committeeId);
  }

  /**
   * Checks if the current user has access to any of the specified committees
   * @param committeeIds - Array of committee IDs to check
   * @returns True if user has access to at least one committee
   */
  public hasAnyCommitteeAccess(committeeIds: string[]): boolean {
    const accessList = this.getCommitteeAccess();
    return committeeIds.some(id => accessList.includes(id));
  }

  /**
   * Gets SQL filter string for committee access
   * This can be used in data queries to filter results to only committees the user has access to
   * @param columnName - Name of the committee column in the query (default: 'CommitteeID')
   * @returns SQL filter string or empty string if no access restrictions
   */
  public getCommitteeAccessFilter(columnName: string = 'CommitteeID'): string {
    const accessList = this.getCommitteeAccess();

    if (accessList.length === 0) {
      // If no access, return a filter that will return no results
      return `${columnName} = ''`;
    }

    // Create IN clause with the accessible committee IDs
    // Convert to string first, then escape single quotes
    const quotedIds = accessList.map(id => {
      const idStr = String(id);
      return `'${idStr.replace(/'/g, "''")}'`;
    }).join(', ');
    return `${columnName} IN (${quotedIds})`;
  }

  /**
   * Refreshes the committee access for the current user
   * Should be called after user changes or impersonation changes
   */
  public async refreshCommitteeAccess(): Promise<void> {
    await this.loadCommitteeAccess();
  }

  /**
   * Clears the committee access (used during logout)
   */
  public clearCommitteeAccess(): void {
    this.committeeAccessSubject.next([]);
  }

  /**
   * Initializes the service and sets up subscriptions
   */
  private initializeService(): void {
    // Subscribe to active user changes
    this.activeUserService.activeUser$.subscribe(activeUserInfo => {
      if (activeUserInfo) {
        // Load committee access when active user changes
        this.loadCommitteeAccess();
      } else {
        // Clear access when no active user
        this.clearCommitteeAccess();
      }
    });
  }

  /**
   * Loads committee access for the current active user
   */
  private async loadCommitteeAccess(): Promise<void> {
    try {
      this.isLoadingSubject.next(true);

      const activeUser = this.activeUserService.getActiveUser();
      if (!activeUser?.Email) {
        LogError('UserCommitteeAccessService: No active user email available');
        this.committeeAccessSubject.next([]);
        return;
      }

      LogStatus(`UserCommitteeAccessService: Loading committee access for user: ${activeUser.Name} (${activeUser.Email})`);

      // Use the UserAuthService to get committee access
      const committeeIds = await this.userAuthService.getUserCommitteeAccess(activeUser);

      // Update the subject with the new access list
      this.committeeAccessSubject.next(committeeIds);

      LogStatus(`UserCommitteeAccessService: Loaded committee access for ${activeUser.Name}: ${committeeIds.length} committees accessible`);

    } catch (error) {
      LogError('UserCommitteeAccessService: Error loading committee access', undefined, error);
      this.committeeAccessSubject.next([]);
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  /**
   * Gets detailed committee access information (including committee names)
   * @returns Promise resolving to detailed committee access information
   */
  public async getDetailedCommitteeAccess(): Promise<PersonChapterLinkResult[]> {
    try {
      const activeUser = this.activeUserService.getActiveUser();
      if (!activeUser?.Email) {
        LogError('UserCommitteeAccessService: No active user email available for detailed access');
        return [];
      }

      // Get the MemberID (PersonID) from ExtendedUsers first, fallback to MembershipRoster
      // Try ExtendedUsers first (faster)
      let memberId: number | null = null;

      if (activeUser.ID) {
        // Try ExtendedUsers using User_ID
        const extendedUsersResult = await this.dataFetchService.fetchEntityData<any>(
          'ExtendedUsers',
          `User_ID = '${activeUser.ID}'`,
          '',
          'simple'
        );

        if (extendedUsersResult.length > 0 && extendedUsersResult[0].PersonID) {
          memberId = parseInt(extendedUsersResult[0].PersonID, 10);
          LogStatus(`UserCommitteeAccessService: Found PersonID ${memberId} in ExtendedUsers`);
        }
      }

      // Fallback to MembershipRoster if not found in ExtendedUsers
      if (!memberId) {
        LogStatus(`UserCommitteeAccessService: PersonID not in ExtendedUsers, checking MembershipRoster`);
        const memberRecords = await this.dataFetchService.fetchEntityData<any>(
          'MembershipRosters',
          `Email = '${activeUser.Email.replace(/'/g, "''")}'`,
          '',
          'simple'
        );

        if (memberRecords.length === 0) {
          LogError(`UserCommitteeAccessService: No MembershipRoster record found for email: ${activeUser.Email}`);
          return [];
        }

        memberId = memberRecords[0].MemberID;
      }

      if (!memberId) {
        LogError(`UserCommitteeAccessService: No MemberID/PersonID found for user: ${activeUser.Email}`);
        return [];
      }

      // Execute PersonChapterLink query to get detailed information
      const queryResult = await this.dataFetchService.executeQuery({
        QueryName: QueryNames.PersonChapterLink,
        Parameters: {
          MemberID: memberId,
          Email: activeUser.Email
        }
      });

      if (!queryResult.success || !queryResult.data) {
        LogError(`UserCommitteeAccessService: PersonChapterLink query failed: ${queryResult.error}`);
        return [];
      }

      // Cast the data to the expected type
      return queryResult.data as PersonChapterLinkResult[];

    } catch (error) {
      LogError('UserCommitteeAccessService: Error getting detailed committee access', undefined, error);
      return [];
    }
  }
}